/**
 * mk-discount-form
 * Application et retrait d'un code de réduction depuis le panier tiroir.
 *
 * API : /cart/update.js accepte un paramètre `discount` depuis le 21/05/2025.
 *   { discount: 'CODE' } -> applique
 *   { discount: '' }     -> retire TOUTES les réductions par code
 * cf. https://shopify.dev/docs/api/ajax/reference/cart#update-discounts-in-the-cart
 *
 * VALIDATION : on ne peut pas se fier à cart.discount_codes, qui reste vide sur
 * cette boutique (la remise remonte via cart_level_discount_applications). On
 * compare donc cart.total_discount avant / après, exposé par Liquid dans
 * l'attribut data-total-discount.
 *   - apply  : si le total de remise n'augmente pas -> code invalide
 *   - remove : si le total de remise ne baisse pas  -> remise automatique,
 *              non retirable depuis le panier
 */
class MkDiscountForm extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[name="discount_code"]');
    this.button = this.querySelector('.mk-discount__submit');
    this.errorEl = this.querySelector('.mk-discount__error');

    if (this.button) {
      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        this.apply();
      });
    }

    if (this.input) {
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.apply();
        }
      });
    }

    this.bindRemoveButtons();
  }

  /** Les boutons vivent dans .cart-drawer__footer, hors de ce custom element. */
  bindRemoveButtons() {
    document.querySelectorAll('.mk-discount__remove').forEach((btn) => {
      if (btn.dataset.mkBound === '1') return;
      btn.dataset.mkBound = '1';
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.removeCode(btn);
      });
    });
  }

  get currentDiscount() {
    const host = document.querySelector('mk-discount-form');
    return host ? parseInt(host.dataset.totalDiscount || '0', 10) : 0;
  }

  showError(message) {
    const el = document.querySelector('mk-discount-form .mk-discount__error');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  setBusy(el, busy) {
    if (el) el.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  async setDiscount(code) {
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: code }),
    });

    if (response.ok) return;

    // Repli ancienne route (/discount/none ne fonctionne plus depuis 08/2025).
    const fallback = code === '' ? 'MK_REMOVE_DISCOUNT_CODES' : code;
    await fetch(`/discount/${encodeURIComponent(fallback)}?redirect=/cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
    });
  }

  /** Re-rend les zones du tiroir dépendantes du panier. */
  async refresh() {
    const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');

    const selectors = ['.mk-progress', 'cart-drawer-items', '.cart-drawer__footer'];
    for (const selector of selectors) {
      const target = document.querySelector(selector);
      const source = html.querySelector(selector);
      if (target && source) target.replaceWith(source);
    }

    this.bindRemoveButtons();
    return this.currentDiscount;
  }

  async apply() {
    const code = ((this.input && this.input.value) || '').trim();
    if (!code) return;

    this.showError('');
    this.setBusy(this.button, true);

    const before = this.currentDiscount;

    try {
      await this.setDiscount(code);
      const after = await this.refresh();

      if (after <= before) {
        this.showError('Ce code est invalide ou non applicable à votre panier.');
      }
    } catch (error) {
      console.error('[mk-discount] apply', error);
      this.showError('Une erreur est survenue. Réessayez.');
      this.setBusy(this.button, false);
    }
  }

  async removeCode(btn) {
    this.showError('');
    this.setBusy(btn, true);

    const before = this.currentDiscount;

    try {
      await this.setDiscount('');
      const after = await this.refresh();

      if (after >= before && before > 0) {
        this.showError(
          "Cette réduction est automatique : elle ne peut pas être retirée depuis le panier."
        );
      }
    } catch (error) {
      console.error('[mk-discount] remove', error);
      this.showError('Impossible de retirer le code. Réessayez.');
      this.setBusy(btn, false);
    }
  }
}

if (!customElements.get('mk-discount-form')) {
  customElements.define('mk-discount-form', MkDiscountForm);
}
